<template>
  <div class="account-pages my-5 pt-sm-5">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6 col-xl-5">
          <div class="text-center mb-5">
            <router-link
              to="/"
              class="auth-logo mb-5 d-block"
            >
              <img
                src="@/assets/images/logo-big.png"
                alt
                height="30"
                class="logo"
              >
            </router-link>
            <h4>{{ $t('login.title') }}</h4>
          </div>
          <div class="card">
            <div class="card-body p-4">
              <div class="p-3">
                <div
                  v-if="loadingAuthToken"
                  class="wmin-sm-400"
                >
                  <div class="alert alert-primary mb-0">
                    {{ $t(`login.oauth_loading`) }}
                  </div>
                </div>
                <form
                  v-else
                  name="login"
                  :data-vv-scope="formName"
                  action="#"
                  method="post"
                  @submit.prevent="onSubmit"
                >
                  <template v-if="modeOTP">
                    <div class="form-group">
                      <label>{{ $t(`login.form.confirmation.label`) }}</label>
                      <div class="input-group mb-3 bg-soft-light input-group-lg rounded-lg">
                        <div class="input-group-prepend">
                          <span class="input-group-text border-light text-muted">
                            <i class="ri-lock-2-line" />
                          </span>
                        </div>
                        <input
                          v-model="itemData.confirmation"
                          v-validate="'required'"
                          tabindex="1"
                          type="text"
                          name="confirmation"
                          :data-vv-as="$t(`login.form.confirmation.label`).toLowerCase()"
                          :data-vv-scope="formName"
                          :class="{ 'is-invalid': errors.has(`${formName}.confirmation`) }"
                          class="form-control bg-soft-light border-light"
                          :placeholder="$t('login.form.confirmation.placeholder')"
                        >
                        <span
                          v-if="errors.has(`${formName}.confirmation`)"
                          class="invalid-feedback"
                          v-text="errors.first(`${formName}.confirmation`)"
                        />
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="form-group">
                      <label>{{ $t(`login.form.email.label`) }}</label>
                      <div class="input-group mb-3 bg-soft-light input-group-lg rounded-lg">
                        <div class="input-group-prepend">
                          <span class="input-group-text border-light text-muted">
                            <i class="ri-mail-line" />
                          </span>
                        </div>
                        <input
                          v-model="itemData.email"
                          v-validate="'required|email'"
                          tabindex="1"
                          type="email"
                          name="email"
                          :data-vv-as="$t(`login.form.email.label`).toLowerCase()"
                          :data-vv-scope="formName"
                          :class="{ 'is-invalid': errors.has(`${formName}.email`) }"
                          class="form-control bg-soft-light border-light"
                          :placeholder="$t('login.form.email.placeholder')"
                        >
                        <span
                          v-if="errors.has(`${formName}.email`)"
                          class="invalid-feedback"
                          v-text="errors.first(`${formName}.email`)"
                        />
                      </div>
                    </div>

                    <div class="form-group mb-4">
                      <div class="float-right">
                        <a
                          href="https://my.messaggio.com/permission/password-recovery"
                          tabindex="4"
                          class="text-muted font-size-13"
                        >
                          {{ $t('login.form.forgotlink') }}
                        </a>
                      </div>
                      <label>{{ $t('login.form.password.label') }}</label>
                      <div class="input-group mb-3 bg-soft-light input-group-lg rounded-lg">
                        <div class="input-group-prepend">
                          <span class="input-group-text border-light text-muted">
                            <i class="ri-lock-2-line" />
                          </span>
                        </div>
                        <input
                          v-model="itemData.password"
                          v-validate="'required'"
                          tabindex="2"
                          type="password"
                          name="password"
                          :data-vv-as="$t(`login.form.password.label`).toLowerCase()"
                          :data-vv-scope="formName"
                          :class="{ 'is-invalid': errors.has(`${formName}.password`) }"
                          class="form-control bg-soft-light border-light"
                          :placeholder="$t('login.form.password.placeholder')"
                        >
                        <span
                          v-if="errors.has(`${formName}.password`)"
                          class="invalid-feedback"
                          v-text="errors.first(`${formName}.password`)"
                        />
                      </div>
                    </div>
                  </template>
                  <div>
                    <button
                      class="btn btn-primary btn-block"
                      :disabled="submit"
                      tabindex="3"
                      type="submit"
                    >
                      {{ signInBtn }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>¬

<script>
import {mapActions} from "vuex";
import {alert} from '@pnotify/core';
import FormMixins from "../../mixins/FormMixins";
import catchDispatch, {catchDispatchErrorMsg} from "../../utils/catchDispatch";

export default {
  mixins: [FormMixins],
  data() {
    return {
      formName: 'login',
      submit: false,
      loadingAuthToken: false,
      modeOTP: false,
      itemData: {
        email: '',
        password: '',
        confirmation: undefined,
      },
    };
  },
  computed: {
    signInBtn() {
      const code = this.modeOTP
        ? 'login.form.button.text_mode_otp'
        : `login.form.button.text`;
      return this.$t(code);
    },
  },
  created() {
    this.$route.query['auth-token'] && this.authToken(this.$route.query['auth-token']);
  },
  methods: {
    ...mapActions('auth', [
      'logIn',
    ]),
    authToken(token) {
      this.loadingAuthToken = true;
      this.$store.dispatch('auth/token', token)
        .then(() => this.$router.push(this.$route.query.redirect || '/'))
        .catch(e => {
          catchDispatch.call(this, e);
          this.loadingAuthToken = false;
        });
    },
    async onSubmit() {
      const valid = await this.onSubmitValid();
      if (!valid) return;

      this
        .logIn(this.itemData)
        .then(() => this.$router.push(this.$route.query.redirectFrom || {name: "home"}))
        .catch(e => {
          if (e.response.status === 403) {
            return alert({
              title: this.$t('login.errors.error403.title'),
              text: this.$t('login.errors.error403.description'),
              addClass: 'bg-danger text-white',
            });
          }

          const eMsg = catchDispatchErrorMsg.call(this, e).toLowerCase();

          if (eMsg.includes('messaggio_otp')) {
            this.modeOTP = true;
            this.$nextTick(() => this.$refs.confirmation.focus());
            return;
          }

          if (eMsg.includes('max rate limiting reached')) {
            return alert({
              title: this.$t('home.error'),
              text: this.$t(`login.errors.max_rate_limiting_reached.title`),
              addClass: 'bg-danger text-white',
            });
          }

          catchDispatch.call(this, e);
        })
        .finally(() => this.submit = false);
    },
  },
};
</script>
